import React, { useState } from "react";
import { FaMapMarkerAlt, FaInstagram, FaWhatsapp } from "react-icons/fa";
import { HiOutlineExternalLink, HiOutlineClipboardCopy, HiCheck } from "react-icons/hi";
import useConfigSitio from "../../hooks/useConfigSitio";

// ========== FALLBACKS EN CASO DE INTERNET LENTO ==========
const FALLBACKS = {
  address: "Tabancura 1091, Vitacura, Santiago",
  horario: "Lun–Vie 7:30–20:00 hrs. Sáb y Dom cerrado.",
  instagram: "https://instagram.com/ekine.cl",
  whatsapp: "https://wa.me/56976604702",
  map_embed: `
    <iframe 
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4003.926992513893!2d-70.5348328!3d-33.3829459!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9662c9e89ea96c37%3A0xaa824f77b0666a66!2sTabancura%201091!5e1!3m2!1ses!2scl!4v1764216035785!5m2!1ses!2scl"
      style="border:0;width:100%;height:100%;border-radius:24px;"
      allowfullscreen=""
      loading="lazy"
    ></iframe>
  `
};

export default function Ubicacion() {
  const { config, loading } = useConfigSitio();
  const [copied, setCopied] = useState(false);

  // ⛑ Usa config si existe — sino usa fallback
  const address = config?.ubicacion_texto || FALLBACKS.address;
  const horario = config?.horario_texto || FALLBACKS.horario;
  const instagram = config?.instagram || FALLBACKS.instagram;

  // WhatsApp limpio
  const whatsapp =
    "https://wa.me/" +
    (config?.whatsapp?.replace("+", "") || "56976604702");

  // Mapa dinámico con fallback SIEMPRE seguro
  const MAP_EMBED = config?.map_embed || FALLBACKS.map_embed;

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const fotoUbicacion = config?.ubicacion_foto || "/fotos/IMG_1879.jpeg";

  return (
    <div className="relative min-h-screen pt-28 md:pt-36 pb-20 px-6 md:px-12 lg:px-20 overflow-hidden text-[#0b2052] bg-[#E5E5E5]">

      {/* Título */}
      <div className="text-center">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#00698F]">
          ¿Dónde estamos ubicados?
        </h1>
        <p className="mt-3 text-lg text-[#0b2052]">
          Encuéntranos fácil y contáctanos por nuestras redes.
        </p>
      </div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">

        {/* COLUMNA IZQUIERDA */}
        <div className="lg:col-span-5 space-y-8">

          {/* Dirección */}
          <div className="bg-white rounded-3xl shadow-xl p-6 border border-zinc-100">
            <h2 className="text-xl font-semibold flex items-center gap-2 text-[#0b2052] mb-4">
              <span className="h-2.5 w-2.5 rounded-full bg-[#06b6fa]" />
              Dirección
            </h2>

            <p className="flex items-start gap-3 text-[17px] text-[#0b2052]">
              <FaMapMarkerAlt className="text-[#06b6fa] mt-1 shrink-0" />
              {address}
            </p>

            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href={config?.googleMaps || "#"}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-white font-semibold bg-[#06b6fa] shadow hover:shadow-lg"
              >
                <HiOutlineExternalLink /> Abrir en Google Maps
              </a>

              <button
                onClick={copyAddress}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-[#06b6fa] text-[#0b2052] hover:bg-gray-50"
              >
                {copied ? <HiCheck /> : <HiOutlineClipboardCopy />}
                {copied ? "Copiado" : "Copiar dirección"}
              </button>
            </div>
          </div>

          {/* Horario */}
          <div className="bg-white rounded-3xl shadow-xl p-6 border border-zinc-100">
            <h2 className="text-xl font-semibold text-[#0b2052] mb-2">
              🕒 Horario de Atención
            </h2>
            <p className="text-[17px] text-gray-700">{horario}</p>
          </div>

          {/* Redes */}
          <div className="bg-white rounded-3xl shadow-xl p-6 border border-zinc-100">
            <h2 className="text-xl font-semibold text-[#0b2052] mb-3">
              🌐 Redes Sociales
            </h2>

            <ul className="space-y-3 text-[17px] text-gray-700">
              <li>
                <a
                  href={instagram}
                  className="flex items-center gap-2 hover:text-[#06b6fa]"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FaInstagram className="text-[#06b6fa]" />
                  {instagram
                    ?.replace("https://www.instagram.com/", "")
                    ?.replace("https://instagram.com/", "")
                    ?.replace("www.instagram.com/", "")
                    ?.replace("instagram.com/", "")
                    ?.replace("/", "")}
                </a>
              </li>

              <li>
                <a
                  href={whatsapp}
                  className="flex items-center gap-2 hover:text-[#06b6fa]"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FaWhatsapp className="text-[#06b6fa]" />
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* COLUMNA DERECHA */}
        <div className="lg:col-span-7">

          {/* Mapa + Foto */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_330px] gap-6 items-start">

            {/* MAPA */}
            <div className="w-full h-[330px] sm:h-[380px] lg:h-[450px] rounded-3xl shadow-xl border border-zinc-200 overflow-hidden">
              <div
                className="w-full h-full [&_*]:w-full [&_*]:h-full [&_*]:max-w-none [&_*]:rounded-3xl"
                dangerouslySetInnerHTML={{ __html: MAP_EMBED }}
              />
            </div>

            {/* FOTO */}
              <img
                src={fotoUbicacion}
                className="w-full lg:w-[300px] h-[220px] sm:h-[260px] lg:h-[450px] rounded-3xl shadow-xl object-cover"
                alt="Entrada del edificio"
                onError={(e) => {
                  e.currentTarget.src = "/fotos/IMG_1879.jpeg";
                }}
              />
          </div>

          {/* Botones abajo */}
          <div className="mt-4 flex flex-wrap items-center gap-3 justify-center lg:justify-start pt-2">
            <a
              href={config?.googleMaps}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold hover:text-[#06b6fa] text-[#0b2052]"
            >
              Ver indicaciones paso a paso →
            </a>

            <span className="text-gray-400">•</span>

            <button
              onClick={copyAddress}
              className="text-sm font-semibold text-gray-700 hover:text-[#06b6fa]"
            >
              {copied ? "Dirección copiada" : "Copiar dirección"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
